import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  forgotPassword,
  verifyForgotPasswordOtp,
  resetPassword,
  clearError,
} from "@/store/slices/authSlice";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Eye, EyeOff } from "lucide-react";
import { Link } from "react-router-dom";

const ForgotPasswordForm = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading, error, success } = useSelector((state) => state.auth);

  const [identifier, setIdentifier] = useState("");
  const [showOtp, setShowOtp] = useState(false);
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [otpVerified, setOtpVerified] = useState(false);
  const [passwords, setPasswords] = useState({
    newPassword: "",
    confirmPassword: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [customError, setCustomError] = useState("");
  const [customSuccess, setCustomSuccess] = useState("");

  const handleSendCode = async (e) => {
    e.preventDefault();
    dispatch(clearError());
    setCustomError("");
    try {
      await dispatch(forgotPassword({ identifier })).unwrap();
      setShowOtp(true);
      setCustomSuccess("Verification code sent successfully!");
    } catch (err) {
      setCustomError(
        err?.message || "No active user found with this phone/email."
      );
      setShowOtp(false);
    }
  };

  // Step 2: OTP input changes
  const handleOtpChange = (index, value) => {
    if (/^[0-9]?$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);

      if (value && index < 5) {
        const nextInput = document.getElementById(`otp-${index + 1}`);
        if (nextInput) nextInput.focus();
      }
      if (!value && index > 0) {
        const prevInput = document.getElementById(`otp-${index - 1}`);
        if (prevInput) prevInput.focus();
      }
    }
  };

  // Step 3: Verify OTP
  const handleVerifyOtp = async () => {
    const enteredOtp = otp.join("");
    if (enteredOtp.length !== 6) {
      setCustomError("Please enter a 6-digit OTP.");
      return;
    }

    setCustomError("");
    try {
      await dispatch(
        verifyForgotPasswordOtp({ identifier, otp: enteredOtp })
      ).unwrap();
      setOtpVerified(true);
      dispatch(clearError());
      setCustomSuccess("OTP verified successfully!");
    } catch (err) {
      setCustomError(
        err?.message ||
          "No active user found with this phone/email or wrong OTP."
      );
      setOtpVerified(false);
    }
  };

  // Step 4: Reset Password
  const handleResetPassword = async () => {
    if (passwords.newPassword !== passwords.confirmPassword) {
      setCustomError("Passwords do not match.");
      return;
    }
    if (passwords.newPassword.length < 6) {
      setCustomError("The password field must be at least 6 characters.");
      return;
    }

    setCustomError("");
    try {
      await dispatch(
        resetPassword({
          identifier,
          password: passwords.newPassword,
          password_confirmation: passwords.confirmPassword,
          otp: otp.join(""),
        })
      ).unwrap();
      setCustomSuccess("Password reset successful! Redirecting to login...");
      // Clear states
      setShowOtp(false);
      setOtpVerified(false);
      setIdentifier("");
      setOtp(["", "", "", "", "", ""]);
      setPasswords({ newPassword: "", confirmPassword: "" });
      // Redirect after short delay
      setTimeout(() => navigate("/login"), 2000);
    } catch (err) {
      setCustomError(
        err?.message ||
          "Failed to reset password. Make sure your password is at least 6 characters."
      );
    }
  };

  const handleBack = () => {
    if (otpVerified) {
      setOtpVerified(false);
      setPasswords({ newPassword: "", confirmPassword: "" });
      setCustomError("");
      setCustomSuccess("");
    } else if (showOtp) {
      setShowOtp(false);
      setOtp(["", "", "", "", "", ""]);
      setCustomError("");
      setCustomSuccess("");
    } else {
      navigate("/login");
    }
  };

  return (
    <div className="space-y-4" style={{ marginTop: "15px" }}>
      {/* Global API error */}
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Local custom error */}
      {customError && (
        <Alert variant="destructive">
          <AlertDescription>{customError}</AlertDescription>
        </Alert>
      )}

      {/* Success messages */}
      {(success || customSuccess) && (
        <Alert variant="success">
          <AlertDescription>{customSuccess || success}</AlertDescription>
        </Alert>
      )}

      <form
        className="space-y-4"
        style={{ marginTop: "20px" }}
        onSubmit={(e) => e.preventDefault()}
      >
        {/* Identifier Field */}
        {!otpVerified && (
          <div className="space-y-2">
            <Input
              id="identifier"
              type="text"
              placeholder="Enter phone number"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              onInput={(e) => {
                e.target.value = e.target.value.replace(/\D/g, "").slice(0, 10);
              }}
              required
              readOnly={showOtp}
            />
          </div>
        )}

        {/* Send Verification Code Button */}
        {!showOtp && !otpVerified && (
          <Button
            type="button"
            className="w-full"
            onClick={handleSendCode}
            disabled={loading || !identifier}
          >
            {loading ? "Sending..." : "Send Verification Code"}
          </Button>
        )}

        {/* OTP Input & Verify Button */}
        {showOtp && !otpVerified && (
          <>
            <div className="flex justify-between gap-2 mt-4">
              {otp.map((value, index) => (
                <Input
                  key={index}
                  id={`otp-${index}`}
                  type="text"
                  maxLength={1}
                  value={value}
                  onChange={(e) => handleOtpChange(index, e.target.value)}
                  className="text-center w-12"
                  required
                />
              ))}
            </div>
            <Button
              type="button"
              className="w-full mt-4"
              onClick={handleVerifyOtp}
              disabled={loading || otp.join("").length !== 6}
            >
              Verify Code
            </Button>
          </>
        )}

        {/* New Password & Confirm Password Fields */}
        {otpVerified && (
          <div className="space-y-4 mt-4">
            <input type="hidden" value={identifier} />

            {/* New Password */}
            <div className="relative">
              <Input
                type={showPassword ? "text" : "password"}
                placeholder="New Password"
                value={passwords.newPassword}
                onChange={(e) =>
                  setPasswords({ ...passwords, newPassword: e.target.value })
                }
                required
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center focus:outline-none"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? (
                  <EyeOff className="h-4 w-4 text-gray-500" />
                ) : (
                  <Eye className="h-4 w-4 text-gray-500" />
                )}
              </button>
            </div>

            {/* Confirm Password */}
            <div className="relative">
              <Input
                type={showConfirmPassword ? "text" : "password"}
                placeholder="Confirm Password"
                value={passwords.confirmPassword}
                onChange={(e) =>
                  setPasswords({
                    ...passwords,
                    confirmPassword: e.target.value,
                  })
                }
                required
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center focus:outline-none"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? (
                  <EyeOff className="h-4 w-4 text-gray-500" />
                ) : (
                  <Eye className="h-4 w-4 text-gray-500" />
                )}
              </button>
            </div>

            <Button
              type="button"
              className="w-full"
              onClick={handleResetPassword}
              disabled={
                loading || !passwords.newPassword || !passwords.confirmPassword
              }
            >
              Change Password
            </Button>
          </div>
        )}
      </form>

      <div className="text-center text-sm mt-1">
        <Link to="/login" className="text-primary underline">
          Back to Login
        </Link>
      </div>
    </div>
  );
};

export default ForgotPasswordForm;
